import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, DownloadSimple, ArrowLeft } from "@phosphor-icons/react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { PLATFORMS } from "../lib/meta";

export default function ReportPage() {
  const { org, activeClient, activeClientId } = useApp();
  const [summary, setSummary] = useState(null);
  const [ai, setAi] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!activeClientId) return;
    api.get(`/insights/summary?client_id=${activeClientId}`).then((r) => setSummary(r.data));
    api.get(`/ai/insights-analysis?client_id=${activeClientId}`).then((r) => setAi(r.data?.headline ? r.data : null));
  }, [activeClientId]);

  const downloadCsv = async () => {
    const { data } = await api.get(`/insights/export?client_id=${activeClientId}`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeClient?.name || "client"}-performance.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = summary?.totals || {};
  const engRate = t.impressions ? (((t.likes + t.comments + t.shares) / t.impressions) * 100).toFixed(2) : "0.00";

  return (
    <div className="min-h-screen bg-white" data-testid="report-page">
      <style>{`@media print { .no-print { display: none !important; } body { background: #fff; } }`}</style>
      <div className="no-print sticky top-0 glass-bar border-b border-black/5 px-6 h-14 flex items-center gap-3">
        <button data-testid="report-back-button" onClick={() => navigate("/insights")} className="flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 transition-colors"><ArrowLeft size={15} /> Back to Insights</button>
        <div className="ml-auto flex gap-2">
          <button data-testid="report-csv-button" onClick={downloadCsv} className="flex items-center gap-2 px-4 py-1.5 rounded-full border border-slate-200 text-[13px] font-medium hover:bg-slate-50 transition-colors"><DownloadSimple size={14} /> Export CSV</button>
          <button data-testid="report-print-button" onClick={() => window.print()} className="flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-white text-[13px] font-medium hover:bg-slate-700 transition-colors"><Printer size={14} /> Print / Save PDF</button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-8 py-12">
        <div className="flex items-start justify-between border-b-2 border-slate-900 pb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-slate-400 font-semibold">{org?.name}</p>
            <h1 className="font-display font-bold text-4xl tracking-tight mt-2">{activeClient?.name}</h1>
            <p className="text-sm text-slate-500 mt-1">Social Media Performance Report · {new Date().toLocaleDateString("en", { month: "long", year: "numeric" })}</p>
          </div>
          <span className="w-10 h-10 rounded-lg" style={{ background: activeClient?.color || "#5B6CF0" }} />
        </div>

        <div className="grid grid-cols-3 gap-6 mt-8">
          {[["Total posts", t.posts], ["Impressions", (t.impressions || 0).toLocaleString()], ["Engagement rate", `${engRate}%`],
            ["Likes", (t.likes || 0).toLocaleString()], ["Comments", (t.comments || 0).toLocaleString()], ["Shares", (t.shares || 0).toLocaleString()]].map(([label, v]) => (
            <div key={label}>
              <p className="text-[11px] uppercase tracking-[0.18em] text-slate-400 font-medium">{label}</p>
              <p className="font-display font-bold text-3xl mt-1" data-testid={`report-metric-${label.toLowerCase().replace(/ /g, "-")}`}>{v}</p>
            </div>
          ))}
        </div>

        <h2 className="font-display font-bold text-xl mt-12 mb-4">Performance by platform</h2>
        <table className="w-full text-[13px]">
          <thead><tr className="text-left text-[11px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
            <th className="py-2 font-medium">Platform</th><th className="py-2 font-medium">Posts</th><th className="py-2 font-medium">Impressions</th><th className="py-2 font-medium">Likes</th><th className="py-2 font-medium">Comments</th><th className="py-2 font-medium">Shares</th>
          </tr></thead>
          <tbody>
            {(summary?.by_platform || []).map((p) => (
              <tr key={p.platform} className="border-b border-slate-100">
                <td className="py-2.5 font-medium">{PLATFORMS[p.platform]?.label || p.platform}</td>
                <td className="py-2.5">{p.posts}</td>
                <td className="py-2.5">{p.impressions.toLocaleString()}</td>
                <td className="py-2.5">{p.likes.toLocaleString()}</td>
                <td className="py-2.5">{p.comments.toLocaleString()}</td>
                <td className="py-2.5">{p.shares.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {ai && (
          <div className="mt-12" data-testid="report-ai-insights">
            <h2 className="font-display font-bold text-xl mb-4">Strategy insights</h2>
            <p className="font-display font-bold text-lg text-slate-800 border-l-4 pl-4" style={{ borderColor: activeClient?.color || "#5B6CF0" }}>"{ai.headline}"</p>
            <ul className="mt-4 space-y-2">
              {(ai.findings || []).map((f, i) => (
                <li key={i} className="text-[13px] text-slate-600 leading-relaxed flex gap-2"><span className="font-bold text-slate-400 shrink-0">·</span>{f}</li>
              ))}
            </ul>
            <div className="mt-5 grid sm:grid-cols-3 gap-3">
              {(ai.recommendations || []).map((r, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-4">
                  <p className="text-[12px] font-bold">{r.title}</p>
                  <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{r.detail}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="font-display font-bold text-xl mt-12 mb-4">Top performing posts</h2>
        <div className="space-y-3">
          {(summary?.top_posts || []).map((r, i) => (
            <div key={r.row_id} className="flex items-start gap-4 border border-slate-200 rounded-lg p-4">
              <span className="font-display font-bold text-xl text-slate-300">#{i + 1}</span>
              <div className="flex-1">
                <p className="text-[13px] text-slate-700">{r.post || "—"}</p>
                <p className="text-[11px] text-slate-400 mt-1">{PLATFORMS[r.platform]?.label || r.platform} · {r.date || ""}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-semibold text-[14px]">{(r.impressions || 0).toLocaleString()}</p>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">impressions</p>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-14 pt-6 border-t border-slate-200 text-[11px] text-slate-400">Prepared by {org?.name}. All metrics imported from native platform exports.{ai ? " Strategy analysis is AI-assisted and human-reviewed." : ""}</p>
      </div>
    </div>
  );
}
